import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, googleMapAPIKey } from './../services/firebase';
import { v4 as uuidv4 } from 'uuid';
import { format, isAfter } from 'date-fns';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';

// Components
import Loader from './components/section/Loader';
import EventNotFound from './components/section/EventNotFound';
import InitialRSVPCheckForm from './components/section/InitialRSVPCheckForm';
import EventHeader from './components/section/EventHeader';
import DeletedEventMessage from './components/section/DeletedEventMessage';
import InvitationImageSection from './components/section/InvitationImageSection';
import EventScheduleSection from './components/section/EventScheduleSection';
import MealScheduleList from './components/section/MealScheduleList';
import EventLocationSection from './components/section/EventLocationSection';
import RSVPForm from './components/section/RSVPForm';
import DynamicEventAccessError from './components/section/DynamicEventAccessError';
import YouTubeLiveStreamSection from './components/section/YouTubeLiveStreamSection';
import EventGallerySection from './components/section/EventGallerySection';
import GreetingsMessageBoard from './components/section/GreetingsMessageBoard';

// Types
import { EventData, StoredUserDetails, MealIcons, RSVPResponse } from './types';
// Icons
import { SunIcon, CakeIcon, FireIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { LoadScript, useJsApiLoader } from '@react-google-maps/api';
import { shareContent } from './utils/helpers';

// Define Invitee type
interface Invitee {
  id: string;
  name: string;
  phone: string;
}

// Extend EventData with selectedInvitees
type EventDataWithInvitees = EventData & {
  selectedInvitees?: Invitee[];
  galleryImages?: Array<{ name: string; url: string }>;
  youtubeLiveStreamURL?: string;
};

// Extended StoredUserDetails with viewerId
interface ExtendedStoredUserDetails extends StoredUserDetails {
  viewerId?: string;
}

export default function EventDetails() {
  const { eventID, inviteeID } = useParams<{ eventID: string; inviteeID?: string }>();
  const navigate = useNavigate();

  // State declarations
  const [loading, setLoading] = useState(true);
  const [eventFound, setEventFound] = useState(false);
  const [eventData, setEventData] = useState<EventDataWithInvitees | null>(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const [selectedMeals, setSelectedMeals] = useState<Record<string, string[]>>({});
  const [showInitialForm, setShowInitialForm] = useState(true);
  const [initialFormErrors, setInitialFormErrors] = useState<{ familyName?: string; phoneNumber?: string }>({});
  const [inviteeError, setInviteeError] = useState('');
  const [qrCodeData, setQrCodeData] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);

  // User and RSVP States
  const [viewerId, setViewerId] = useState('');
  const [rsvpStatus, setRsvpStatus] = useState<'confirmed' | 'declined' | ''>('');
  const [familyContactName, setFamilyContactName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [attendeesNumber, setAttendeesNumber] = useState<any>("");
  const [declinedCount, setDeclinedCount] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Event status states
  const [countdown, setCountdown] = useState<{ days: string; hours: string; minutes: string; seconds: string; } | null>(null);
  const [eventStatus, setEventStatus] = useState<'upcoming' | 'live' | 'ended'>('upcoming');
  const [currentDay, setCurrentDay] = useState(0);

  // Refs
  const qrCodeRef = useRef<HTMLDivElement | null>(null);

  // Meal icons mapping
  const mealIcons: MealIcons = {
    breakfast: <SunIcon className="w-6 h-6 text-yellow-500" />,
    lunch: <CakeIcon className="w-6 h-6 text-blue-500" />,
    dinner: <FireIcon className="w-6 h-6 text-red-500" />,
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: googleMapAPIKey,
  });

  const isGoogleScriptLoaded = !!window.google || document.querySelector(`script[src*="maps.googleapis.com"]`);

  // Helper function to get or create viewerId
  const getOrCreateViewerId = (name: string, phone: string): string => {
    // Check if user already has a viewerId in localStorage
    const storedData = localStorage.getItem('eventUserDetails');
    if (storedData) {
      const parsedData: ExtendedStoredUserDetails[] = JSON.parse(storedData);
      const existingEntry = parsedData.find(item =>
        item.eventID === eventID &&
        item.familyName === name &&
        item.phoneNumber === phone
      );
      if (existingEntry?.viewerId) {
        return existingEntry.viewerId;
      }
    }

    // Generate new viewerId
    return uuidv4();
  };

  // Helper function to save user details with viewerId
  const saveUserDetails = (name: string, phone: string, viewerId: string) => {
    const storedData = localStorage.getItem('eventUserDetails');
    const newEntry: ExtendedStoredUserDetails = {
      familyName: name.trim(),
      phoneNumber: phone.trim(),
      eventID: eventID!,
      viewerId
    };

    let updatedData: ExtendedStoredUserDetails[] = [];
    if (storedData) {
      updatedData = JSON.parse(storedData).filter((data: ExtendedStoredUserDetails) =>
        !(data.eventID === eventID && data.familyName === name && data.phoneNumber === phone)
      );
    }
    updatedData.push(newEntry);
    localStorage.setItem('eventUserDetails', JSON.stringify(updatedData));
  };

  // Check existing RSVP response
  const checkExistingRSVPResponse = async (name: string, phone: string) => {
    if (!eventID || !name.trim() || !phone.trim()) return null;

    try {
      const q = query(
        collection(db, 'rsvpResponses'),
        where('eventID', '==', eventID),
        where('userName', '==', name.trim()),
        where('userPhone', '==', phone.trim())
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data() as RSVPResponse;
      }
    } catch (error) {
      console.error('Error checking existing response:', error);
    }
    return null;
  };

  // Handle initial form submission
  const handleInitialSubmit = async (name: string, phone: string) => {
    const errors: typeof initialFormErrors = {};
    if (!name.trim()) errors.familyName = 'Organizer Name is required';
    if (!phone.match(/^\d{10}$/)) errors.phoneNumber = 'Valid 10-digit number required';

    if (Object.keys(errors).length > 0) {
      setInitialFormErrors(errors);
      return;
    }

    setInitialFormErrors({});
    setIsSubmitting(true);

    try {
      // Get or create viewerId
      const userViewerId = getOrCreateViewerId(name, phone);

      // Save user details with viewerId
      saveUserDetails(name, phone, userViewerId);

      // Set states
      setFamilyContactName(name.trim());
      setMobileNumber(phone.trim());
      setViewerId(userViewerId);
      setShowInitialForm(false);

      // Check for existing RSVP response
      const existingResponse = await checkExistingRSVPResponse(name, phone);
      if (existingResponse) {
        setSubmitted(true);
        setRsvpStatus(existingResponse.attending ? 'confirmed' : 'declined');
        setAttendeesNumber(existingResponse.numberOfAttendees || "");
        setDeclinedCount(existingResponse.declinedCount || 1);
        setSelectedMeals(existingResponse.selectedMeals || {});
      }
    } catch (error) {
      console.error('Error handling initial submit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle dynamic invitee ID
  useEffect(() => {
    if (inviteeID && eventData?.selectedInvitees) {
      const invitee = eventData.selectedInvitees.find(inv => inv.id === inviteeID);
      if (invitee) {
        const userViewerId = getOrCreateViewerId(invitee.name, invitee.phone);
        setFamilyContactName(invitee.name);
        setMobileNumber(invitee.phone);
        setViewerId(userViewerId);
        setShowInitialForm(false);
        setInviteeError('');

        // Save user details for dynamic invitee
        saveUserDetails(invitee.name, invitee.phone, userViewerId);
      } else {
        setInviteeError('The provided invitee code is invalid. Please contact the event organizer.');
      }
    }
  }, [eventData, inviteeID]);

  // Check localStorage for existing user data
  useEffect(() => {
    if (!eventID || inviteeID) return;

    const storedData = localStorage.getItem('eventUserDetails');
    if (storedData) {
      const parsedData: ExtendedStoredUserDetails[] = JSON.parse(storedData);
      const entryForEvent = parsedData.find(item => item.eventID === eventID);
      if (entryForEvent) {
        setFamilyContactName(entryForEvent.familyName);
        setMobileNumber(entryForEvent.phoneNumber);
        setViewerId(entryForEvent.viewerId || '');
        setShowInitialForm(false);
      }
    }
  }, [eventID, inviteeID]);

  // Fetch event data
  useEffect(() => {
    if (!eventID) {
      navigate("/404");
      return;
    }

    const fetchEvent = async () => {
      try {
        const docRef = doc(db, "events", eventID);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.deleted === 1) {
            setIsDeleted(true);
            setEventFound(true);
            setLoading(false);
            return;
          }

          const processedData: EventDataWithInvitees = {
            id: docSnap.id,
            title: data.title,
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate(),
            days: data.days.map((day: any) => ({
              ...day,
              date: day.date.toDate(),
              startTime: day.startTime.toDate(),
              endTime: day.endTime.toDate(),
              meals: day.meals.map((meal: any) => ({ name: meal.name }))
            })),
            eventAddress: data.eventAddress,
            eventCoOrdinates: data.eventCoOrdinates,
            invitationFileURL: data.invitationFileURL,
            qrCodeType: data.qrCodeType,
            attendeesCount: data.expectedGuests || 0,
            deleted: data.deleted || 0,
            selectedInvitees: data.selectedInvitees || [],
            galleryImages: data.galleryImages || [],
            youtubeLiveStreamURL: data.youtubeLiveStreamURL || ''
          };

          setEventData(processedData);
          setEventFound(true);
          updateEventStatus(processedData);
        } else {
          setEventFound(false);
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        setEventFound(false);
      } finally {
        setLoading(false);
      }
    };

    const updateEventStatus = (data: EventData) => {
      const updateStatus = () => {
        const now = new Date();
        if (now > data.endDate) {
          setEventStatus('ended');
          setCountdown(null);
        } else if (now >= data.startDate) {
          setEventStatus('live');
          setCountdown(null);
          const dayDiff = Math.floor((now.getTime() - data.startDate.getTime()) / (1000 * 3600 * 24));
          setCurrentDay(Math.min(dayDiff + 1, data.days.length));
        } else {
          const diff = data.startDate.getTime() - now.getTime();
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);

          setCountdown({
            days: days.toString().padStart(2, '0'),
            hours: hours.toString().padStart(2, '0'),
            minutes: minutes.toString().padStart(2, '0'),
            seconds: seconds.toString().padStart(2, '0')
          });
        }
      };

      updateStatus();
      const interval = setInterval(updateStatus, 1000);
      return () => clearInterval(interval);
    };

    fetchEvent();
  }, [eventID, navigate]);

  // RSVP Form configuration
  const rsvpFormStateObj = {
    rsvpStatus,
    familyContactName,
    mobileNumber,
    attendeesNumber,
    declinedCount,
    fieldErrors,
    isSubmitting,
    submitted,
    selectedMeals
  };

  const rsvpFormActionsObj = {
    setRsvpStatus: (status: 'confirmed' | 'declined' | '') => {
      setRsvpStatus(status);
      if (status === 'confirmed' && eventData) {
        const defaultMeals = eventData.days.reduce((acc, day) => ({
          ...acc,
          [day.date.toISOString()]: day.meals.map(meal => meal.name)
        }), {} as Record<string, string[]>);
        setSelectedMeals(defaultMeals);
      } else {
        setSelectedMeals({});
      }
    },
    setFamilyContactName,
    setMobileNumber,
    setAttendeesNumber,
    setDeclinedCount,
    setFieldErrors,
    handleMealSelect: (dayId: string | number, mealName: string) => setSelectedMeals(prev => ({
      ...prev,
      [dayId]: prev[dayId]?.includes(mealName)
        ? prev[dayId].filter(m => m !== mealName)
        : [...(prev[dayId] || []), mealName]
    }))
  };

  // Handle RSVP submission
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!eventID) return;

    const errors: Record<string, string> = {};
    if (!rsvpStatus) errors.rsvpStatus = 'Please select attendance status';
    if (!familyContactName.trim()) errors.familyContactName = 'Contact Person name is required';
    if (mobileNumber.length !== 10) errors.mobileNumber = 'Valid 10-digit number required';
    if (rsvpStatus === 'confirmed' && attendeesNumber < 1) errors.attendeesNumber = 'Number of attendees required';
    if (rsvpStatus === 'declined' && declinedCount < 1) errors.declinedCount = 'Number of people not attending required';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      const baseUrl = window.location.origin + "/apps/eventiq/event";
      // Check for existing response
      const existingResponse = await checkExistingRSVPResponse(familyContactName, mobileNumber);

      let finalViewerId = viewerId;
      let qrCodeURL = '';
      let eventAttended = false;

      // If existing response found, reuse its data
      if (existingResponse) {
        finalViewerId = existingResponse.viewerId || finalViewerId;
        eventAttended = existingResponse.eventAttended || false;
        if (existingResponse.qrCodeURL && rsvpStatus === 'confirmed') {
          qrCodeURL = existingResponse.qrCodeURL;
        }
      }

      // Create new QR code URL if needed
      if (!qrCodeURL && rsvpStatus === 'confirmed') {
        qrCodeURL = `${baseUrl}/${eventID}/entry/${finalViewerId}`;
      }

      const responseData = {
        eventID,
        viewerId: finalViewerId,
        attending: rsvpStatus === 'confirmed',
        rsvpType: "personal" as const,
        userName: familyContactName.trim(),
        userEmail: "",
        userPhone: mobileNumber.trim(),
        selectedMeals: rsvpStatus === 'confirmed' ? selectedMeals : {},
        timestamp: new Date(),
        numberOfAttendees: rsvpStatus === 'confirmed' ? attendeesNumber : 0,
        declinedCount: rsvpStatus === 'declined' ? declinedCount : 0,
        qrCodeURL: rsvpStatus === 'confirmed' ? qrCodeURL : '',
        eventAttended: rsvpStatus === 'declined' ? false : eventAttended
      };

      let docRef = existingResponse
        ? doc(db, 'rsvpResponses', (await getDocs(query(collection(db, 'rsvpResponses'), where('eventID', '==', eventID), where('userName', '==', familyContactName.trim()), where('userPhone', '==', mobileNumber.trim())))).docs[0].id)
        : doc(collection(db, 'rsvpResponses'));

      await setDoc(docRef, responseData);
      setSubmitted(true);
      setViewerId(finalViewerId);

      // Update localStorage with viewerId
      saveUserDetails(familyContactName, mobileNumber, finalViewerId);

      // Show QR code for confirmed responses
      if (rsvpStatus === 'confirmed') {
        setQrCodeData(qrCodeURL);
        setShowQRModal(true);
      }

    } catch (error) {
      console.error('Error saving response:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShare = async (type: 'link' | 'image' | 'qr') => {
    try {
      if (!eventData) return;
      await shareContent(type, {
        title: eventData.title,
        invitationFileURL: eventData.invitationFileURL,
        currentUrl: window.location.href,
        qrElement: type === 'qr' ? qrCodeRef.current : undefined
      });
    } catch (err) {
      console.error('Sharing failed:', err);
    }
  };

  const isRSVPOpen = () => {
    if (!eventData) return false;
    const deadline = new Date(eventData.startDate.getTime() - 86400000);
    return isAfter(new Date(), deadline);
  };

  if (loading) return <Loader />;
  if (isDeleted) return <DeletedEventMessage />;

  if (eventData?.qrCodeType === 'dynamic' && !inviteeID) {
    return <DynamicEventAccessError />;
  }

  if (showInitialForm) return (
    <div>
      {inviteeError && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
          <p className="font-bold">Invitation Error</p>
          <p>{inviteeError}</p>
        </div>
      )}
      <InitialRSVPCheckForm
        onSubmit={handleInitialSubmit}
        errors={initialFormErrors}
        loading={isSubmitting}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showInitialForm && eventData &&
          <EventHeader
            eventData={eventData}
            eventStatus={eventStatus}
            handleShare={handleShare}
            countdown={countdown}
            currentDay={currentDay}
          />
        }

        {!eventFound || !eventData ? (
          <EventNotFound />
        ) : (
          <div className="space-y-8">
            {/* Two-column grid for main content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-8">
                <EventScheduleSection eventData={eventData} />
                <InvitationImageSection invitationFileURL={eventData.invitationFileURL} eventTitle={eventData.title} />

                {eventData.galleryImages && eventData.galleryImages.length > 0 && (
                  <EventGallerySection images={eventData.galleryImages} />
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                <MealScheduleList eventData={eventData} mealIcons={mealIcons} />

                {eventData.youtubeLiveStreamURL && (
                  <YouTubeLiveStreamSection url={eventData.youtubeLiveStreamURL} />
                )}

                {!isLoaded && <p>Loading map...</p>}
                {!isGoogleScriptLoaded ? (
                  <LoadScript googleMapsApiKey={googleMapAPIKey}>
                    <EventLocationSection eventData={eventData} />
                  </LoadScript>
                ) : (
                  <EventLocationSection eventData={eventData} />
                )}

                {eventStatus !== 'ended' && (
                  <RSVPForm
                    formState={rsvpFormStateObj}
                    formActions={rsvpFormActionsObj}
                    eventData={eventData}
                    isRSVPOpen={isRSVPOpen}
                    format={format}
                    onSubmit={handleSubmit}
                    mealIcons={mealIcons}
                  />
                )}
              </div>
            </div>

            {/* Full-width Greetings Message Board - Outside the grid */}
            {viewerId && (
              <div className="w-full">
                <GreetingsMessageBoard
                  eventID={eventID!}
                  currentUserName={familyContactName}
                  currentUserPhone={mobileNumber}
                  viewerId={viewerId}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {showQRModal && (
        <QRCodeModal
          qrCodeData={qrCodeData}
          onClose={() => setShowQRModal(false)}
          eventTitle={eventData?.title || ''}
          userName={familyContactName}
        />
      )}
    </div>
  );
}

// QRCodeModal component (keep as is)
interface QRCodeModalProps {
  qrCodeData: string;
  onClose: () => void;
  eventTitle: string;
  userName: string;
}

const QRCodeModal = ({ qrCodeData, onClose, eventTitle, userName }: QRCodeModalProps) => {
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');

  const handleDownloadQR = async () => {
    if (qrCodeRef.current) {
      try {
        const canvas = await html2canvas(qrCodeRef.current);
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `${userName.replace(/\s+/g, '-')}-event-pass.png`;
        link.href = image;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading QR code:', error);
      }
    }
  };

  const handleCopyUrl = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(qrCodeData);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = qrCodeData;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyError('Copy failed. Please try again.');
      setTimeout(() => setCopyError(''), 3000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-xs w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Your Event Entry Pass</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-3xl font-bold leading-none"
          >
            &times;
          </button>
        </div>

        <p className="text-center mb-2 text-gray-700 text-sm truncate">{eventTitle}</p>

        <div ref={qrCodeRef} className="relative flex justify-center mb-4">
          <QRCode
            value={qrCodeData}
            size={220}
            level="H"
          />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="bg-white p-1 rounded-full">
              <div className="bg-gray-200 rounded-full w-10 h-10 flex items-center justify-center">
                <span className="text-xs font-bold text-gray-700">
                  {userName}
                </span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center font-semibold mb-2">{userName}</p>
        <p className="text-sm text-gray-500 text-center mb-6">
          Show this QR code at the event entrance
        </p>

        {copied && (
          <div className="bg-green-100 text-green-700 p-2 rounded mb-3 text-center text-sm">
            URL copied to clipboard!
          </div>
        )}

        {copyError && (
          <div className="bg-red-100 text-red-700 p-2 rounded mb-3 text-center text-sm">
            {copyError}
          </div>
        )}

        <div className="flex justify-between gap-3">
          <button
            onClick={handleDownloadQR}
            className="flex-1 flex flex-col items-center justify-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 p-3 rounded-lg border border-blue-200 transition-colors"
            title="Download QR Code"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span className="text-xs mt-1">Download</span>
          </button>

          <button
            onClick={handleCopyUrl}
            className="flex-1 flex flex-col items-center justify-center gap-1 bg-purple-50 hover:bg-purple-100 text-purple-700 p-3 rounded-lg border border-purple-200 transition-colors"
            title="Copy QR Code URL"
          >
            <ClipboardDocumentIcon className="h-6 w-6" />
            <span className="text-xs mt-1">Copy URL</span>
          </button>
        </div>
      </div>
    </div>
  );
};